import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { Color, FilterInputType, IconName, TabSmallTheme } from '../../enums';
import { FilterDetails, FilterOption, SuggestionItem } from '../../interfaces';
import { Overlay } from '../../theme';
import { UseState } from '../../types';
import { Autosuggest } from '../autosuggest/autosuggest.component';
import { Icon } from '../icon/icon.component';

import {
  ButtonsContainer,
  Container,
  FiltersContainer,
  SingleFilterContainer,
  FilterTitle,
  OptionsContainer,
  StyledButton,
  FullFilterContainer,
  IconContainer,
  StyledButtonClearFilters,
  StyledTabSmall,
  Wrapper,
  InputsContainer,
  StyledInput,
  AutosuggestContainer,
} from './filters.styled';
import { FiltersProps } from './filters.types';

export const Filters: React.FC<FiltersProps> = (props: FiltersProps): JSX.Element => {
  const {
    onFilter,
    onClose,
    filters,
    visible = true,
    defaultFilters = {},
    filtersActions = {},
    className
  }: FiltersProps = props;
  const { t }: TransProps<never> = useTranslation();
  const [dropdownSelectedValue, setDropdownSelectedValue]: UseState<string> = useState<string>('');
  const [selectedFilters, setSelectedFilters]: UseState<Record<string, string[]>> = useState<Record<string, string[]>>({});
  const [inputValidationMessageFrom, setInputValidationMessageFrom]: UseState<string> = useState<string>('');
  const [inputValidationMessageTo, setInputValidationMessageTo]: UseState<string> = useState<string>('');
  const [autosuggestFiltersPrettyValues, setAutosuggestFiltersPrettyValues]: UseState<Record<string, string>>
    = useState<Record<string, string>>({});

  const onClearFilters: () => void = (): void => {
    setSelectedFilters({});
    setDropdownSelectedValue('');
  };

  const onApplyFilters: () => void = (): void => {
    onFilter(selectedFilters);
    setDropdownSelectedValue('');
    onClose();
  };

  const addToSelectedFilters: (
    filterName: string, filterOptions: string, isSingleChoice: boolean, filterPrettyValue?: string
  ) => void = (filterName: string, filterOptions: string, isSingleChoice: boolean, filterPrettyValue?: string): void => {
    if (selectedFilters[filterName]) {
      if (selectedFilters[filterName].length === 1 && selectedFilters[filterName].includes(filterOptions)) {
        let filtersWithoutRemovedFilter: Record<string, string[]> = {};
        Object.keys(selectedFilters).forEach((key: string) => {
          if (key !== filterName) {
            filtersWithoutRemovedFilter = { ...filtersWithoutRemovedFilter, [key]: selectedFilters[key] };
          }
        });
        setSelectedFilters(filtersWithoutRemovedFilter);
      } else if (isSingleChoice) {
        setSelectedFilters(selectedFilters[filterName].includes(filterOptions) 
          ? { 
            ...selectedFilters, 
            [filterName]: [...selectedFilters[filterName]
              .filter((value: string): boolean => value !== filterOptions)]  
          }
          : { ...selectedFilters, [filterName]: [filterOptions] }
        );
      } else { 
        setSelectedFilters(selectedFilters[filterName].includes(filterOptions)
          ? { 
            ...selectedFilters, 
            [filterName]: [...selectedFilters[filterName]
              .filter((value: string): boolean => value !== filterOptions)] 
          }
          : { ...selectedFilters, [filterName]: [...selectedFilters[filterName], filterOptions] }
        );
      }
    } else {
      setSelectedFilters({ ...selectedFilters, [filterName]: [filterOptions] });
    }

    const filterConfig: FilterDetails | undefined = filters.find((filter: FilterDetails): boolean => filter.name === filterName);

    if (filterConfig?.autosuggestLabel && filterPrettyValue) {
      setAutosuggestFiltersPrettyValues({ ...autosuggestFiltersPrettyValues, [filterName]: filterPrettyValue });
    }
  };

  const onInputChange: (
    value: string, type: FilterInputType, filterName: string
  ) => void = (value: string, type: FilterInputType, filterName: string): void => {
    if (!selectedFilters[filterName]) {
      setSelectedFilters({ ...selectedFilters, [`${filterName}${type}`]: [value] });
    }

    if (type === FilterInputType.To 
      && !!selectedFilters[`${filterName}${FilterInputType.From}`] 
      && Number(selectedFilters[`${filterName}${FilterInputType.From}`]) > Number(value)
    ) {
      setInputValidationMessageTo(t('onarte.ui.filters.inputValidationMessageTo'));
    } else if (
      type === FilterInputType.From 
      && !!selectedFilters[`${filterName}${FilterInputType.To}`] 
      && Number(value) > Number(selectedFilters[`${filterName}${FilterInputType.To}`])
    ) {
      setInputValidationMessageFrom(t('onarte.ui.filters.inputValidationMessageFrom'));
    } else {
      setSelectedFilters({ ...selectedFilters, [`${filterName}${type}`]: [value] });
      setInputValidationMessageFrom('');
      setInputValidationMessageTo('');
    }
  };

  useEffect(
    (): void => {
      if (!!Object.entries(defaultFilters).length && !Object.entries(selectedFilters).length) {
        setSelectedFilters(defaultFilters);
      }
    },
    [defaultFilters]
  );

  return (
    <Wrapper>
      <Container className={className} $isVisible={visible}>
        <FiltersContainer>
          {filters.map((filter: FilterDetails): JSX.Element => (
            <FullFilterContainer key={filter.name}>
              <SingleFilterContainer
                $isSelected={dropdownSelectedValue === filter.name}
                onClick={(): void => setDropdownSelectedValue(dropdownSelectedValue === filter.name ? '' : filter.name)}
              >
                <FilterTitle $isSelected={dropdownSelectedValue === filter.name}>{filter.label}</FilterTitle>
                <IconContainer>
                  <Icon
                    name={dropdownSelectedValue === filter.name
                      ? IconName.ArrowUp15
                      : IconName.ArrowDown15
                    }
                    size={16}
                    color={dropdownSelectedValue === filter.name ? Color.Black : Color.Gray500}
                  />
                </IconContainer>
              </SingleFilterContainer>
              {dropdownSelectedValue === filter.name && (
                <>
                  {filter.inputsType && (
                    <InputsContainer>
                      <StyledInput 
                        label={t('onarte.ui.filters.input.from.label')} 
                        type={filter.inputsType} 
                        icon={filter.inputsIcon} 
                        value={
                          selectedFilters[`${filter.name}${FilterInputType.From}`] 
                            ? selectedFilters[`${filter.name}${FilterInputType.From}`][0]
                            :  ''
                        }
                        onBlur={(value: string): void => onInputChange(value, FilterInputType.From, filter.name)} 
                        onChange={filter.inputsType === 'date'
                          ? (value: string): void => onInputChange(value, FilterInputType.From, filter.name)
                          : undefined
                        }
                        withoutMinDate
                        validationMessage={inputValidationMessageFrom}
                        withOnlyNumbersAllowed={filter.inputsType !== 'date'}
                        withPriceParsing={filter.withPriceParsing}
                      />
                      <StyledInput 
                        label={t('onarte.ui.filters.input.to.label')} 
                        type={filter.inputsType} 
                        icon={filter.inputsIcon} 
                        value={
                          selectedFilters[`${filter.name}${FilterInputType.To}`] 
                            ? selectedFilters[`${filter.name}${FilterInputType.To}`][0] 
                            :  ''
                        }
                        onBlur={(value: string): void => onInputChange(value, FilterInputType.To, filter.name)} 
                        onChange={filter.inputsType === 'date' 
                          ? (value: string): void => onInputChange(value, FilterInputType.To, filter.name)
                          : undefined
                        }
                        withoutMinDate
                        validationMessage={inputValidationMessageTo}
                        withOnlyNumbersAllowed={filter.inputsType !== 'date'}
                        withPriceParsing={filter.withPriceParsing}
                      />
                    </InputsContainer>
                  )}
                  {filter.options?.length && (
                    <OptionsContainer>
                      {filter.options && filter.options.map((option: FilterOption): JSX.Element => (
                        <StyledTabSmall
                          key={option.name}
                          tabSmallTheme={
                            selectedFilters[filter.name] && selectedFilters[filter.name].includes(option.name)
                              ? TabSmallTheme.Gray
                              : TabSmallTheme.Transparent
                          }
                          label={option.label}
                          color={option.color}
                          onClick={(): void => addToSelectedFilters(filter.name, option.name, !!filter.isSingleChoice)}
                        />
                      ))}
                    </OptionsContainer>
                  )}
                  {!!(filter.autosuggestLabel && filtersActions[filter.name]?.autosuggestOnValueChange) && (
                    <AutosuggestContainer>
                      <Autosuggest
                        label={filter.autosuggestLabel}
                        initialSuggestionLabel={(selectedFilters[filter.name]?.length && autosuggestFiltersPrettyValues[filter.name])
                          ? autosuggestFiltersPrettyValues[filter.name]
                          : undefined
                        }
                        onSelect={(suggestion: SuggestionItem): void => addToSelectedFilters(
                          filter.name, suggestion?.id ?? '', true, suggestion.label
                        )}
                        onValueInputChangeAsync={filtersActions[filter.name].autosuggestOnValueChange}
                      />
                    </AutosuggestContainer>
                  )}
                </>
              )}
            </FullFilterContainer>
          ))}
        </FiltersContainer>
        <ButtonsContainer>
          <StyledButtonClearFilters data-testid='clear-button' onClick={onClearFilters}>
            {`${t('onarte.ui.filters.clearButtonLabel')} ${
              Object.keys(selectedFilters).length 
                ? `(${Object.keys(selectedFilters).length})` 
                : ''
            }`}
          </StyledButtonClearFilters>
          <StyledButton data-testid='apply-button' onClick={onApplyFilters}>{t('onarte.ui.filters.applyButtonLabel')}</StyledButton>
        </ButtonsContainer>
      </Container>
      <Overlay $isVisible={visible} onClick={onClose} />
    </Wrapper>
  );
};
